import React, { useState } from 'react';
import { X, ShoppingCart, Plus, Minus, Trash2, ArrowRight, Shield, Lock, Tag, Check } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from './ui/sheet';
import FreeShippingBar from './FreeShippingBar';
import { cartApi } from '../services/api';
import { toast } from 'sonner';
import { useSettings } from '../context/SettingsContext';

const CartDrawer = ({ isOpen, onClose, cartItems, onUpdateQuantity, onRemove, onCheckout, sessionId }) => {
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const settings = useSettings();

  const shippingThreshold = settings.free_shipping_threshold || 50;
  const shippingCost = settings.default_shipping_cost || 5.99;
  const subtotal = cartItems.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 0), 0);
  const shipping = subtotal >= shippingThreshold ? 0 : shippingCost;
  const total = Math.max(0, subtotal - discount + shipping);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setIsApplyingCoupon(true);
    try {
      const result = await cartApi.applyCoupon(sessionId, couponCode.trim());
      setAppliedCoupon(couponCode.toUpperCase());
      setDiscount(result.discount_amount || 0);
      toast.success(`Coupon ${couponCode.toUpperCase()} applied! You save $${result.discount_amount.toFixed(2)}`);
      setCouponCode('');
    } catch (error) {
      toast.error(error.message || 'Invalid coupon code');
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = async () => {
    try {
      await cartApi.removeCoupon(sessionId);
      setAppliedCoupon(null);
      setDiscount(0);
      toast.success('Coupon removed');
    } catch (error) {
      console.error('Error removing coupon:', error);
    }
  };

  const handleProceedToCheckout = () => {
    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    if (onCheckout) {
      onCheckout({
        items: cartItems,
        subtotal,
        discount,
        shipping,
        total,
        couponCode: appliedCoupon
      });
    }
    onClose();
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg bg-white dark:bg-gray-900 flex flex-col">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 text-gray-800 dark:text-white">
            <ShoppingCart className="w-5 h-5" />
            Shopping Cart ({cartItems.length})
          </SheetTitle>
        </SheetHeader>

        <div className="flex flex-col flex-1 overflow-hidden">
          {/* Free Shipping Progress */}
          {cartItems.length > 0 && (
            <div className="py-3 flex-shrink-0">
              <FreeShippingBar cartTotal={subtotal} threshold={shippingThreshold} />
            </div>
          )}

          {/* Cart Items */}
          <div className="flex-1 overflow-auto py-4">
            {cartItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
                <ShoppingCart className="w-16 h-16 mb-4 text-gray-300 dark:text-gray-600" />
                <p className="text-lg font-medium">Your cart is empty</p>
                <p className="text-sm">Add some products to get started</p>
                <button 
                  onClick={onClose}
                  className="mt-4 px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                >
                  Continue Shopping
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {cartItems.map((item) => (
                  <div key={item.id} className="flex gap-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                    <img
                      src={item.image || 'https://via.placeholder.com/80'}
                      alt={item.name}
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-800 dark:text-white line-clamp-2">{item.name}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-teal-600 dark:text-teal-400 font-semibold">${(item.price || 0).toFixed(2)}</p>
                        {item.originalPrice && item.originalPrice > item.price && (
                          <p className="text-xs text-gray-400 line-through">${item.originalPrice.toFixed(2)}</p>
                        )}
                      </div>
                      
                      {/* Quantity Controls */}
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => onUpdateQuantity(item.id, Math.max(1, item.quantity - 1))}
                            className="p-1 bg-white dark:bg-gray-700 border dark:border-gray-600 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                            aria-label="Decrease quantity"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="w-8 text-center font-medium text-gray-800 dark:text-white">{item.quantity}</span>
                          <button
                            onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                            className="p-1 bg-white dark:bg-gray-700 border dark:border-gray-600 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                            aria-label="Increase quantity"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <button
                          onClick={() => onRemove(item.id)}
                          className="p-1 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-md transition-colors"
                          aria-label={`Remove ${item.name} from cart`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {cartItems.length > 0 && (
            <div className="border-t dark:border-gray-700 pt-4 pb-6 space-y-4 flex-shrink-0">
              {/* Coupon Code Input */}
              <div className="space-y-2">
                {appliedCoupon ? (
                  <div className="flex items-center justify-between p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
                        {appliedCoupon} applied
                      </span>
                      <span className="text-sm text-emerald-600">(-${discount.toFixed(2)})</span>
                    </div>
                    <button
                      onClick={handleRemoveCoupon}
                      className="text-sm text-emerald-600 hover:text-emerald-700 underline"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Enter coupon code"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        className="w-full pl-10 pr-4 py-2.5 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                    </div>
                    <button
                      onClick={handleApplyCoupon}
                      disabled={isApplyingCoupon || !couponCode.trim()}
                      className="px-4 py-2 bg-gray-800 dark:bg-gray-700 text-white rounded-lg hover:bg-gray-700 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isApplyingCoupon ? '...' : 'Apply'}
                    </button>
                  </div>
                )}
              </div>

              {/* Summary */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Subtotal</span>
                  <span className="font-medium text-gray-800 dark:text-white">${subtotal.toFixed(2)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-emerald-600">
                    <span>Discount</span>
                    <span>-${discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Shipping</span>
                  <span className="font-medium">
                    {shipping === 0 ? (
                      <span className="text-emerald-600 dark:text-emerald-400">FREE</span>
                    ) : (
                      <span className="text-gray-800 dark:text-white">${shipping.toFixed(2)}</span>
                    )}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t dark:border-gray-700 text-base">
                  <span className="font-semibold text-gray-800 dark:text-white">Total</span>
                  <span className="font-bold text-teal-600 dark:text-teal-400">${total.toFixed(2)}</span>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="flex items-center justify-center gap-4 py-2">
                <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                  <Lock className="w-3 h-3" />
                  <span>Secure Checkout</span>
                </div>
                <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                  <Shield className="w-3 h-3" />
                  <span>Money Back Guarantee</span>
                </div>
              </div>

              {/* Checkout Button */}
              <button 
                onClick={handleProceedToCheckout}
                className="w-full flex items-center justify-center gap-2 py-3.5 bg-gradient-to-r from-teal-600 to-emerald-600 text-white font-semibold rounded-xl hover:from-teal-700 hover:to-emerald-700 transition-all shadow-lg shadow-teal-200 dark:shadow-teal-900/30 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
              >
                Proceed to Checkout
                <ArrowRight className="w-4 h-4" />
              </button>

              {/* Payment Methods */}
              <div className="flex items-center justify-center gap-2 pt-2">
                <span className="text-xs text-gray-400">We accept:</span>
                <div className="flex gap-1">
                  {['Visa', 'MC', 'PayPal', 'Apple'].map((method) => (
                    <div key={method} className="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded text-xs text-gray-600 dark:text-gray-400">
                      {method}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
